import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sharedTableStyles } from '../../utils/tableStyles';
import { TableSkeleton } from '../../components/UI/Skeleton/Skeleton';
import RowActionMenu from '../../components/UI/RowActionMenu/RowActionMenu';
import { Modal, Button } from 'react-bootstrap';
import DataTable from 'react-data-table-component';

import PageHeader from '../../components/common/PageHeader';
import '../../App.css';
import '../Announcements/AnnouncementView.scss';

import { useGetNoteListQuery, useDeleteNotesMutation } from '../../redux/apis/files';
import { useIsLoggedInQuery } from '../../redux/apis/authApi';

import { formatDate, paginationRow, skipRow } from '../../utils/handler';
import { BT_BUTTON_LABELS } from '../../utils/constant/buttons';
import { LB_ANNOUNCEMENT_TABLE_COLS } from '../../utils/constant/labels';
import { ROLES, getRecordLocality } from '../../utils/constant/global';
import { ANNOUNCEMENT_TITLE } from '../../utils/constant/header';
import { notification } from '../../utils/notification';
import { TOAST_TYPE } from '../../utils/constant/errorMessage';
import { cleanupOffcanvasBackdrop, hideOffcanvasById } from '../../utils/offcanvasHelpers';

// const { LOADING } = GLOBAL;
const { DELETE_ANNOUNCEMENT, DELETE_ANNOUNCEMENT_CONFORMATION } = ANNOUNCEMENT_TITLE;
const { BTN_ADD_ANNOUNCEMENT, BTN_CANCEL, BTN_DELETE } = BT_BUTTON_LABELS;
const {
  LB_TBL_COL_TITLE,
  LB_TBL_COL_CREATED_AT,
  LB_TBL_COL_UPDATED_AT,
  LB_TBL_COL_NATIONALITY,
  LB_ANNOUNCEMENT_ACTION
} = LB_ANNOUNCEMENT_TABLE_COLS;
const { TOAST_SUCCESS } = TOAST_TYPE;

const announcementTableStyles = {
  ...sharedTableStyles,
  headCells: {
    style: {
      ...sharedTableStyles.headCells.style,
      color: '#334155',
      fontWeight: 700
    }
  },
  rows: {
    ...sharedTableStyles.rows,
    style: {
      ...sharedTableStyles.rows.style,
      color: '#6b7280'
    }
  },
  cells: {
    style: {
      ...sharedTableStyles.cells.style,
      color: '#6b7280',
      fontWeight: 600
    }
  }
};

const initialState = {
  loading: false,
  currentPage: 1,
  rowsPerPage: 10,
  isNext: false,
  showDeleteModal: false,
  noteIdToDelete: null,
  selectedNote: null
};

export default function AnnouncementAdmin() {
  const navigate = useNavigate();
  const [state, setState] = useState(initialState);
  const { ADMIN, HR } = ROLES;

  const { data } = useIsLoggedInQuery();
  const [deleteNote] = useDeleteNotesMutation();
  const {
    data: noteListData,
    isLoading,
    isFetching,
    refetch
  } = useGetNoteListQuery({
    skip: skipRow(state.currentPage, state.rowsPerPage),
    limit: state.rowsPerPage
  });

  useEffect(() => {
    refetch();
    setState({ ...state, isNext: noteListData?.isNext });
  }, [state.rowsPerPage, state.currentPage, state.search, state.sortDirection]);
  const handleViewNote = (note) => {
    setState({ ...state, selectedNote: note });
  };

  useEffect(() => {
    const offcanvasEl = document.getElementById('offcanvasRight');
    if (!offcanvasEl) return undefined;

    const handleHidden = () => cleanupOffcanvasBackdrop();
    offcanvasEl.addEventListener('hidden.bs.offcanvas', handleHidden);
    return () => offcanvasEl.removeEventListener('hidden.bs.offcanvas', handleHidden);
  }, []);

  const handleCloseAnnouncementView = () => hideOffcanvasById('offcanvasRight');

  const roleName = data?.data?.hrxRole?.roleName;
  const isHrOrAdmin = roleName === ADMIN || roleName === HR;

  const columnT = [
    {
      name: LB_TBL_COL_TITLE,
      selector: (row) => <span className="announcement-title">{row.title}</span>,
      sortable: false
    },
    ...(isHrOrAdmin
      ? [
          {
            name: LB_TBL_COL_CREATED_AT,
            selector: (row) => <span className={row.txtClass}>{formatDate(row?.createdAt)}</span>,
            sortable: false
          },
          {
            name: LB_TBL_COL_UPDATED_AT,
            selector: (row) => <span className={row.txtClass}>{formatDate(row?.updatedAt)}</span>,
            sortable: false
          },
          {
            name: LB_TBL_COL_NATIONALITY,
            selector: (row) => (
              <span className={row.txtClass}>{getRecordLocality(row).join(', ') || '-'}</span>
            ),
            sortable: false
          }
        ]
      : []),
    {
      name: LB_ANNOUNCEMENT_ACTION,
      selector: () => {},
      sortable: false,
      cell: (row) => (
        <RowActionMenu
          actions={[
            {
              label: 'View',
              icon: 'icofont-eye',
              onClick: () => handleViewNote(row),
              dataAttrs: {
                'data-bs-toggle': 'offcanvas',
                'data-bs-target': '#offcanvasRight',
                'aria-controls': 'offcanvasRight'
              }
            },
            {
              label: 'Edit',
              icon: 'icofont-edit',
              variant: 'success',
              hidden: !isHrOrAdmin,
              onClick: () => navigate(`/announcement/edit/${row._id}`, { state: { note: row } })
            },
            {
              label: 'Delete',
              icon: 'icofont-trash',
              variant: 'danger',
              hidden: !isHrOrAdmin,
              onClick: () => {
                setState({ ...state, showDeleteModal: true, noteIdToDelete: row?._id });
              }
            }
          ]}
        />
      )
    }
  ];

  const handleCancelDelete = () => {
    setState({ ...state, showDeleteModal: false });
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await deleteNote(state.noteIdToDelete);
      notification(response?.data?.message, TOAST_SUCCESS);
      setState({ ...state, showDeleteModal: false });
      refetch();
    } catch (error) {
      console.error('Error deleting file:', error);
      setState({ ...state, showDeleteModal: false });
    }
  };

  const handleChangePage = (page) => {
    setState({ ...state, currentPage: page });
  };

  const handleChangeRowsPerPage = (newRowsPerPage) => {
    setState({ ...state, rowsPerPage: newRowsPerPage, currentPage: 1 });
  };

  return (
    <>
      <div className="container-xxl announcement-page">
        <PageHeader
          headerTitle="Announcement"
          renderRight={() => {
            return (
              <div className="col-auto d-flex w-sm-100">
                {isHrOrAdmin && (
                  <button
                    type="button"
                    className="app-table-toolbar__add-btn"
                    onClick={() => navigate('/announcement/create')}
                  >
                    <i className="icofont-plus-circle"></i>
                    {BTN_ADD_ANNOUNCEMENT}
                  </button>
                )}
              </div>
            );
          }}
        />
        {/* <div className="announcement-surface"> */}
        {/* <div className="announcement-surface__head">
            <div />
          </div> */}

        <div className="announcement-surface__body">
          <div className="announcement-table">
            <DataTable
              customStyles={announcementTableStyles}
              columns={columnT}
              data={noteListData?.data || []}
              defaultSortField="title"
              pagination
              selectableRows={false}
              paginationTotalRows={paginationRow(
                state.isNext,
                state.rowsPerPage,
                state.currentPage,
                data?.data?.length
              )}
              paginationPerPage={state.rowsPerPage}
              paginationRowsPerPageOptions={[5, 10, 20, 30]}
              paginationComponentOptions={{
                noRowsPerPage: false,
                rangeSeparatorText: 'of',
                selectAllRowsItem: state.isNext,
                selectAllRowsItemText: 'All',
                paginationButtonsDisabled: state.isNext
              }}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
              className="table myDataTable table-hover align-middle mb-0 d-row nowrap dataTable no-footer dtr-inline"
              highlightOnHover={true}
              progressPending={isLoading || isFetching ? true : false}
              progressComponent={<TableSkeleton columns={columnT.length} />}
            />
          </div>
        </div>
        {/* </div> */}
      </div>

      <Modal show={state.showDeleteModal} onHide={handleCancelDelete}>
        <Modal.Header closeButton>
          <Modal.Title>{DELETE_ANNOUNCEMENT}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{DELETE_ANNOUNCEMENT_CONFORMATION}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCancelDelete}>
            {BTN_CANCEL}
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete} style={{ color: 'white' }}>
            {BTN_DELETE}
          </Button>
        </Modal.Footer>
      </Modal>

      <div
        className={`offcanvas offcanvas-end offcanvas-announcement`}
        tabIndex="-1"
        id="offcanvasRight"
        aria-labelledby="offcanvasRightLabel"
      >
        <div className="announcement-view">
          <div className="announcement-view__header">
            <div className="announcement-view__header-content">
              <span className="announcement-view__icon" aria-hidden="true">
                <i className="icofont-bullhorn" />
              </span>
              <div className="announcement-view__copy">
                <h5 className="announcement-view__title" id="offcanvasRightLabel">
                  {state?.selectedNote?.title}
                </h5>
                <div className="announcement-view__meta">
                  {state?.selectedNote?.createdAt ? (
                    <span>
                      <i className="icofont-calendar" aria-hidden />{' '}
                      {formatDate(state.selectedNote.createdAt)}
                    </span>
                  ) : null}
                  {state?.selectedNote?.updatedAt ? (
                    <span>
                      <i className="icofont-refresh" aria-hidden />{' '}
                      {formatDate(state.selectedNote.updatedAt)}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
            <button
              type="button"
              className="btn-close announcement-view__close"
              aria-label="Close"
              onClick={handleCloseAnnouncementView}
            ></button>
          </div>
          <div className="announcement-view__body">
            {state.selectedNote && (
              <div className="announcement-view__card">
                <div
                  className="announcement-html"
                  dangerouslySetInnerHTML={{
                    __html: state?.selectedNote?.description
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
